// ProviderOnboarding.js - Multi step onboarding for new caretakers
// Step 1 — Welcome screen
// Step 2 — Profile photo upload (required)
// Step 3 — Bio and service description
// Step 4 — Supplies checklist acknowledgment
// Step 5 — Ready to go
// Smooth slide animations between steps
// Auth: uses onAuthStateChanged (not auth.currentUser) per project rules

import React, { useState, useEffect } from 'react';
import { auth, db, storage } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';

function ProviderOnboarding() {
  const navigate = useNavigate();

  // Auth state — resolved via onAuthStateChanged, never auth.currentUser directly
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Tracks which step we are on — 1 through 5
  const [step, setStep] = useState(1);

  // Animation state
  const [visible, setVisible] = useState(true);
  const [slideDirection, setSlideDirection] = useState('right');

  // Profile photo state
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [photoUrl, setPhotoUrl] = useState(null);

  // Bio state
  const [bio, setBio] = useState('');

  // Supplies checklist acknowledgment state
  const [suppliesChecked, setSuppliesChecked] = useState(false);
  const [suppliesError, setSuppliesError] = useState('');

  // Loading and error state for final step
  const [finishing, setFinishing] = useState(false);
  const [finishError, setFinishError] = useState('');

  // Resolve auth state on mount — redirect to login if no session
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
      if (!firebaseUser) {
        navigate('/login');
        return;
      }
      setUser(firebaseUser);
      setAuthLoading(false);
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fade in on first load
  useEffect(() => {
    setVisible(false);
    setTimeout(() => setVisible(true), 50);
  }, []);

  // Transitions to the next step with smooth animation
  const goToNextStep = () => {
    setSlideDirection('left');
    setVisible(false);

    setTimeout(() => {
      setStep(prev => prev + 1);
      setSlideDirection('right');
      setTimeout(() => setVisible(true), 50);
    }, 350);
  };

  // Handles photo file selection and shows a preview
  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setPhotoError('Please upload an image file (JPG or PNG).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setPhotoError('Photo must be under 10 MB.');
      return;
    }

    setPhotoFile(file);
    setPhotoError('');

    // FileReader creates a local preview URL before uploading
    const reader = new FileReader();
    reader.onload = (e) => {
      setPhotoPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Uploads photo to Firebase Storage and saves URL to Firestore
  // Uses `user` state set by onAuthStateChanged — never auth.currentUser
  const handlePhotoUpload = async () => {
    // Photo is required — show error if none selected
    if (!photoFile) {
      setPhotoError('Please select a photo to continue');
      return;
    }

    if (!user) return;

    try {
      setUploadingPhoto(true);
      setPhotoError('');

      // Upload to Firebase Storage
      const photoRef = ref(storage, `profile-photos/${user.uid}`);
      await uploadBytes(photoRef, photoFile);

      // Get the public download URL
      const downloadURL = await getDownloadURL(photoRef);

      // Save URL to provider's Firestore document
      await updateDoc(doc(db, 'users', user.uid), {
        photoUrl: downloadURL
      });

      setPhotoUrl(downloadURL);

      // Move to next step
      goToNextStep();

    } catch (err) {
      console.error('Error uploading photo:', err);
      setPhotoError('Upload failed. Please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Saves bio and marks onboarding complete
  // Uses `user` state set by onAuthStateChanged — never auth.currentUser
  const handleFinish = async () => {
    if (!user) return;

    try {
      setFinishing(true);
      setFinishError('');

      // Save bio and mark onboarding complete
      await updateDoc(doc(db, 'users', user.uid), {
        bio: bio.trim(),
        onboardingComplete: true,
      });

      // Short delay for completion animation, then go to terms acknowledgment
      setTimeout(() => {
        navigate('/terms-acknowledge-provider');
      }, 1500);

    } catch (err) {
      console.error('Error finishing onboarding:', err);
      setFinishing(false);
      setFinishError('Something went wrong. Please try again.');
    }
  };

  // CSS classes for smooth slide animation
  const stepStyle = {
    opacity: visible ? 1 : 0,
    transform: visible
      ? 'translateX(0)'
      : slideDirection === 'left'
        ? 'translateX(-40px)'
        : 'translateX(40px)',
    transition: 'opacity 0.35s ease, transform 0.35s ease',
    width: '100%',
    maxWidth: '520px',
  };

  // Show spinner while Firebase auth resolves
  if (authLoading) {
    return (
      <div className="onboarding-page">
        <a href="/" className="auth-logo"><img src="/logo.png" alt="Evermark Memorial" /></a>
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-page">

      {/* Logo */}
      <a href="/" className="auth-logo"><img src="/logo.png" alt="Evermark Memorial" /></a>

      {/* Progress dots */}
      <div className="onboarding-dots">
        {[1, 2, 3, 4, 5].map(dot => (
          <div
            key={dot}
            className={`onboarding-dot ${dot <= step ? 'onboarding-dot-active' : ''}`}
          />
        ))}
      </div>

      {/* Animated step container using inline styles for reliability */}
      <div style={stepStyle}>

        {/* ========== STEP 1 — WELCOME ========== */}
        {step === 1 && (
          <div className="onboarding-card">
            <div className="onboarding-icon">🌿</div>
            <h2>Welcome to Evermark Memorial</h2>
            <p className="onboarding-subtitle">
              You're about to join a community of trusted caretakers who help
              families honor their loved ones from anywhere in the world.
            </p>

            <div className="onboarding-info-list">
              <div className="onboarding-info-item">
                <span className="onboarding-info-icon">📸</span>
                <div>
                  <h4>Build trust with a photo</h4>
                  <p>Customers feel more comfortable knowing who is visiting their loved one's grave</p>
                </div>
              </div>
              <div className="onboarding-info-item">
                <span className="onboarding-info-icon">⭐</span>
                <div>
                  <h4>Earn great reviews</h4>
                  <p>Higher ratings mean more jobs and better visibility in your area</p>
                </div>
              </div>
              <div className="onboarding-info-item">
                <span className="onboarding-info-icon">💰</span>
                <div>
                  <h4>Get paid for your care</h4>
                  <p>Earn $41-83 per visit depending on the service type</p>
                </div>
              </div>
            </div>

            <button className="btn-primary btn-full" onClick={goToNextStep}>
              Let's Get Started →
            </button>
          </div>
        )}

        {/* ========== STEP 2 — PROFILE PHOTO (REQUIRED) ========== */}
        {step === 2 && (
          <div className="onboarding-card">
            <div className="onboarding-icon">📷</div>
            <h2>Add Your Profile Photo</h2>
            <p className="onboarding-subtitle">
              Families want to know who is caring for their loved ones.
              A clear friendly photo is <strong>required</strong> to start receiving jobs.
            </p>

            {/* Photo upload area */}
            <div className="onboarding-photo-area">
              {photoPreview ? (
                <div className="onboarding-photo-preview">
                  <img
                    src={photoPreview}
                    alt="Profile preview"
                    className="onboarding-preview-img"
                  />
                  <button
                    className="onboarding-retake-btn"
                    onClick={() => {
                      setPhotoFile(null);
                      setPhotoPreview(null);
                      setPhotoError('');
                    }}
                  >
                    Choose a different photo
                  </button>
                </div>
              ) : (
                <label className="onboarding-upload-area">
                  <div className="onboarding-upload-icon">👤</div>
                  <p className="onboarding-upload-text">Tap to upload a photo</p>
                  <p className="onboarding-upload-sub">JPG or PNG — clear face shot</p>
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handlePhotoSelect}
                  />
                </label>
              )}
            </div>

            {/* Photo error */}
            {photoError && <div className="auth-error">{photoError}</div>}

            {/* Tips */}
            <div className="onboarding-tips">
              <p className="onboarding-tips-title">Tips for a great photo:</p>
              <p>✓ Clear face with good lighting</p>
              <p>✓ Friendly and professional</p>
              <p>✓ No sunglasses or filters</p>
            </div>

            {/* Upload button — no skip option since photo is required */}
            <button
              className="btn-primary btn-full"
              onClick={handlePhotoUpload}
              disabled={uploadingPhoto}
            >
              {uploadingPhoto ? 'Uploading your photo...' : 'Save Photo & Continue →'}
            </button>

          </div>
        )}

        {/* ========== STEP 3 — BIO ========== */}
        {step === 3 && (
          <div className="onboarding-card">
            <div className="onboarding-icon">✍️</div>
            <h2>Tell Families About Yourself</h2>
            <p className="onboarding-subtitle">
              A short bio helps customers feel confident choosing you.
              What makes you a great caretaker?
            </p>

            <div className="auth-field" style={{ textAlign: 'left' }}>
              <label>Your Bio <span className="optional">(optional)</span></label>
              <textarea
                className="booking-textarea"
                placeholder="e.g. I'm a retired groundskeeper with 10 years of experience caring for cemeteries. I treat every grave with the same respect I would want for my own family..."
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={5}
                maxLength={300}
              />
              <p style={{ fontSize: '12px', color: '#aaa', textAlign: 'right', marginTop: '4px' }}>
                {bio.length}/300
              </p>
            </div>

            <div className="onboarding-examples">
              <p className="onboarding-tips-title">What to include:</p>
              <p>✓ Your background or experience</p>
              <p>✓ Why this work is meaningful to you</p>
              <p>✓ How you approach each visit</p>
            </div>

            <button className="btn-primary btn-full" onClick={goToNextStep}>
              Continue →
            </button>

            <p className="auth-switch">
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '14px' }}
                onClick={goToNextStep}
              >
                Skip for now
              </button>
            </p>
          </div>
        )}

        {/* ========== STEP 4 — WHAT YOU'LL NEED ========== */}
        {step === 4 && (
          <div className="onboarding-card">
            <div className="onboarding-icon">🧰</div>
            <h2>What You'll Need</h2>
            <p className="onboarding-subtitle">
              Before accepting jobs, make sure you have these supplies ready. These are your responsibility as an independent caretaker.
            </p>

            {/* Informational supply list — not interactive checkboxes */}
            <div className="supplies-list">
              {[
                'Stone-safe cleaning solution (D/2 Biological Solution recommended)',
                'Distilled water',
                'Soft bristle brush',
                'Microfiber cloths',
                'Gloves',
                'Smartphone with camera for before/after photos',
              ].map((item, i) => (
                <div key={i} className="supplies-item">
                  <span className="supplies-check-icon">✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>

            {/* Flower allowance note */}
            <p className="supplies-note">
              For Tribute Visit jobs, a $22 flower allowance will be released to you when you claim the job to cover the cost of a fresh floral arrangement.
            </p>

            {/* Acknowledgment checkbox — must be checked to continue */}
            <label className="supplies-acknowledge">
              <input
                type="checkbox"
                checked={suppliesChecked}
                onChange={(e) => {
                  setSuppliesChecked(e.target.checked);
                  if (e.target.checked) setSuppliesError('');
                }}
              />
              <span>I understand I am responsible for providing my own supplies and agree to the Evermark Memorial caretaker standards.</span>
            </label>

            {suppliesError && <p className="supplies-error">{suppliesError}</p>}

            <button
              className="btn-primary btn-full"
              style={{ marginTop: '20px' }}
              onClick={() => {
                if (!suppliesChecked) {
                  setSuppliesError('Please confirm the above before continuing.');
                  return;
                }
                goToNextStep();
              }}
            >
              Continue →
            </button>
          </div>
        )}

        {/* ========== STEP 5 — ALL DONE ========== */}
        {step === 5 && (
          <div className="onboarding-card onboarding-complete">
            <div className="onboarding-complete-icon">
              {finishing ? '⏳' : '🎉'}
            </div>
            <h2>
              {finishing ? 'Setting up your dashboard...' : 'You\'re all set!'}
            </h2>
            <p className="onboarding-subtitle">
              {finishing
                ? 'Just a moment while we get everything ready for you.'
                : 'Your caretaker profile is ready. Start browsing available jobs in your area and earn money helping families honor their loved ones.'}
            </p>

            {/* Top-level error for finish failures */}
            {finishError && <div className="auth-error">{finishError}</div>}

            {!finishing && (
              <>
                <div className="onboarding-ready-list">
                  <div className="onboarding-ready-item">
                    <span className="onboarding-ready-check">✅</span>
                    <span>Profile created</span>
                  </div>
                  {photoUrl && (
                    <div className="onboarding-ready-item">
                      <span className="onboarding-ready-check">✅</span>
                      <span>Profile photo added</span>
                    </div>
                  )}
                  {bio && (
                    <div className="onboarding-ready-item">
                      <span className="onboarding-ready-check">✅</span>
                      <span>Bio added</span>
                    </div>
                  )}
                  <div className="onboarding-ready-item">
                    <span className="onboarding-ready-check">✅</span>
                    <span>Ready to claim jobs</span>
                  </div>
                </div>

                <button
                  className="btn-primary btn-full"
                  onClick={handleFinish}
                >
                  Go to My Dashboard →
                </button>
              </>
            )}

            {finishing && (
              <div className="loading-spinner" style={{ margin: '20px auto' }}></div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

export default ProviderOnboarding;
